import React, { useCallback, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import Papa from 'papaparse';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { promoCampaignQueryKeys } from '~/hooks/apis/promoCampaigns';

import { type PromoCampaignPresenter, updatePromoCampaign } from '~/services/LuxLoyalty';

interface PromoCampaignAddUsersDialogProps {
  open: boolean;
  campaign: PromoCampaignPresenter | null;
  onClose: () => void;
}

const UUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const BATCH_SIZE = 10000;
const CSV_HEADERS = ['user_id', 'userId', 'customer_id', 'customerId'];

export default function PromoCampaignAddUsersDialog({ open, campaign, onClose }: PromoCampaignAddUsersDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [csvFileName, setCsvFileName] = useState<string | null>(null);
  const [csvUserIds, setCsvUserIds] = useState<Array<string>>([]);
  const [failedBatchIndex, setFailedBatchIndex] = useState<number | null>(null);
  const [storedBatches, setStoredBatches] = useState<Array<Array<string>>>([]);
  const [invalidIds, setInvalidIds] = useState<Array<string>>([]);
  const [manualId, setManualId] = useState('');
  const [manualIdError, setManualIdError] = useState<string | null>(null);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);

  const resetState = useCallback(() => {
    setIsUploading(false);
    setProgress(0);
    setStatusMessage(null);
    setErrorMessage(null);
    setCsvFileName(null);
    setCsvUserIds([]);
    setFailedBatchIndex(null);
    setStoredBatches([]);
    setInvalidIds([]);
    setManualId('');
    setManualIdError(null);
    setWarningMessage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const parseUserIds = useCallback((ids: Array<string>) => {
    const trimmed = ids.map((id) => id.trim()).filter(Boolean);
    const deduped = Array.from(new Set(trimmed));
    const valid = deduped.filter((id) => UUID_REGEX.test(id));
    const invalid = deduped.filter((id) => !UUID_REGEX.test(id));
    return { valid, invalid };
  }, []);

  const handleCsvFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      setCsvFileName(file.name);
      setErrorMessage(null);
      setCsvUserIds([]);
      setInvalidIds([]);

      const parseWithHeader = () =>
        new Promise<Papa.ParseResult<Record<string, string>>>((resolve) => {
          Papa.parse<Record<string, string>>(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => resolve(results),
          });
        });

      const parseWithoutHeader = () =>
        new Promise<Papa.ParseResult<Array<string>>>((resolve) => {
          Papa.parse<Array<string>>(file, {
            header: false,
            skipEmptyLines: true,
            complete: (results) => resolve(results),
          });
        });

      (async () => {
        const headerResults = await parseWithHeader();
        const fields = headerResults.meta.fields || [];
        const headerField = fields.find((field) => CSV_HEADERS.includes(field));

        let userIds: Array<string>;
        if (headerField) {
          userIds = headerResults.data
            .map((row) => row?.[headerField] ?? '')
            .map((value) => String(value).trim())
            .filter(Boolean);
        } else {
          const noHeaderResults = await parseWithoutHeader();
          const rawIds = noHeaderResults.data
            .map((row) => (Array.isArray(row) ? row[0] : ''))
            .map((value) => String(value).trim())
            .filter(Boolean);

          // Skip the first row if it looks like a header (not a valid UUID)
          if (rawIds.length > 0 && !UUID_REGEX.test(rawIds[0])) {
            userIds = rawIds.slice(1);
          } else {
            userIds = rawIds;
          }
        }

        const { valid, invalid } = parseUserIds(userIds);
        setCsvUserIds(valid);
        setInvalidIds(invalid);
        if (invalid.length > 0) {
          enqueueSnackbar(`${invalid.length} invalid IDs were ignored`, { variant: 'warning' });
        }
      })().catch((error) => {
        enqueueSnackbar(`Failed to parse CSV: ${error.message || 'Unknown error'}`, { variant: 'error' });
      });
    },
    [enqueueSnackbar, parseUserIds],
  );

  const buildBatches = useCallback((ids: Array<string>) => {
    const batches: Array<Array<string>> = [];
    for (let i = 0; i < ids.length; i += BATCH_SIZE) {
      batches.push(ids.slice(i, i + BATCH_SIZE));
    }
    return batches;
  }, []);

  const processBatches = useCallback(
    async (batches: Array<Array<string>>, startIndex = 0) => {
      if (!campaign) return;

      setIsUploading(true);
      setErrorMessage(null);
      setStatusMessage(null);
      setFailedBatchIndex(null);

      let receivedTotal = 0;
      let createdTotal = 0;
      let duplicatesTotal = 0;

      for (let index = startIndex; index < batches.length; index += 1) {
        const batch = batches[index];
        try {
          const response = await updatePromoCampaign(campaign.id, { campaign: {}, addUserIds: batch });
          const stats = response.result.assignmentStats;
          receivedTotal += stats.received;
          createdTotal += stats.created;
          duplicatesTotal += stats.duplicatesSkipped;

          setProgress(Math.round(((index + 1) / batches.length) * 100));
          setStatusMessage(
            `Processed batch ${index + 1}/${
              batches.length
            } - created: ${createdTotal}, duplicates skipped: ${duplicatesTotal}`,
          );
        } catch (error: any) {
          setFailedBatchIndex(index);
          setErrorMessage(error.message || 'Failed to upload batch');
          setIsUploading(false);
          return;
        }
      }

      setIsUploading(false);
      setStatusMessage(
        `Completed assignment - received: ${receivedTotal}, created: ${createdTotal}, duplicates skipped: ${duplicatesTotal}`,
      );
      enqueueSnackbar('Users assigned successfully', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: promoCampaignQueryKeys.all });
    },
    [campaign, enqueueSnackbar, queryClient],
  );

  const handleManualSubmit = async () => {
    if (!campaign) {
      return;
    }

    const trimmed = manualId.trim();
    if (!trimmed) {
      setManualIdError('Customer ID is required');
      return;
    }
    if (!UUID_REGEX.test(trimmed)) {
      setManualIdError('Must be a valid UUID');
      return;
    }

    setManualIdError(null);
    setIsUploading(true);
    setErrorMessage(null);
    setStatusMessage(null);
    setWarningMessage(null);

    try {
      const response = await updatePromoCampaign(campaign.id, { campaign: {}, addUserIds: [trimmed] });
      const stats = response.result.assignmentStats;
      setIsUploading(false);

      if (stats.duplicatesSkipped > 0) {
        setWarningMessage('Customer was already assigned to this campaign');
      } else {
        setStatusMessage('Customer added successfully');
        enqueueSnackbar('User assigned successfully', { variant: 'success' });
      }

      setManualId('');
      queryClient.invalidateQueries({ queryKey: promoCampaignQueryKeys.all });
    } catch (error: unknown) {
      setIsUploading(false);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to add user');
    }
  };

  const handleSubmit = async () => {
    if (!campaign) return;

    if (csvUserIds.length === 0) {
      setErrorMessage('Upload a CSV file with at least one valid customer ID');
      return;
    }

    const batches = buildBatches(csvUserIds);
    setStoredBatches(batches);
    await processBatches(batches);
  };

  const handleRetry = async () => {
    if (failedBatchIndex == null || storedBatches.length === 0) return;
    await processBatches(storedBatches, failedBatchIndex);
  };

  const handleClose = () => {
    if (isUploading) {
      return;
    }
    resetState();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Users to Promo Campaign</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Campaign
          </Typography>
          <Typography>{campaign ? campaign.name : '-'}</Typography>

          <Typography variant="subtitle2">Single Entry</Typography>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              label="Customer ID (UUID)"
              value={manualId}
              onChange={(e) => {
                setManualId(e.target.value);
                setManualIdError(null);
              }}
              error={!!manualIdError}
              helperText={manualIdError}
              size="small"
              fullWidth
              disabled={isUploading}
            />
            <Button
              variant="contained"
              onClick={handleManualSubmit}
              disabled={isUploading || !manualId.trim()}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Add
            </Button>
          </Stack>

          <Divider>or</Divider>

          <Typography variant="subtitle2">Bulk CSV Upload</Typography>
          <Stack spacing={1}>
            <Button variant="outlined" component="label" disabled={isUploading}>
              Select CSV File
              <input ref={fileInputRef} type="file" accept=".csv" hidden onChange={handleCsvFileChange} />
            </Button>
            <Typography variant="body2" color="text.secondary">
              {csvFileName
                ? `Selected: ${csvFileName} (${csvUserIds.length.toLocaleString()} valid IDs)`
                : 'Upload a CSV with a user_id/customer_id column. Only UUIDs are accepted.'}
            </Typography>
          </Stack>

          {invalidIds.length > 0 && <Alert severity="warning">{invalidIds.length} invalid IDs were ignored.</Alert>}

          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {warningMessage && <Alert severity="warning">{warningMessage}</Alert>}
          {statusMessage && <Alert severity="info">{statusMessage}</Alert>}
          {isUploading && <LinearProgress variant="determinate" value={progress} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={handleClose} disabled={isUploading}>
          Close
        </Button>
        {failedBatchIndex != null && (
          <Button variant="contained" onClick={handleRetry} disabled={isUploading}>
            Retry Failed Batch
          </Button>
        )}
        <Button variant="contained" onClick={handleSubmit} disabled={isUploading || csvUserIds.length === 0}>
          Start Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
}
